'use client';
import dynamic from 'next/dynamic';
import { useState, useEffect, ReactNode, useRef, useMemo } from 'react';
import { PlanCard, PlanCardProps } from '@/components/atoms/PlanCard';
const Button = dynamic(() => import('de-sxm-reactlib-2/components/atoms/Button/Button'));
const InvalidFeedback = dynamic(
    () => import('de-sxm-reactlib-2/components/atoms/Inputs/InvalidFeedback/InvalidFeedback'),
);
import useInheritParams from '@/hooks/useInheritParams';
import { URL_INHERIT_PARAMS, VALIDITY } from '@/constants';
import styles from './planselector.module.scss';

const {
    mainWrapper,
    selectorWrapper,
    offerSection,
    planCardWrapper,
    inputStyles,
    offerTitleStyles,
    offerPriceStyles,
    errorMessageStyles,
    headlineStyles,
    offerDetailStyles,
    selectButtonStyles,
    inputWrapper,
    textContentWrapper,
} = styles;

type DefaultCtaData = {
    href: string;
    linkText: string;
    ariaLabel: string;
};

type PlanData = {
    cta: DefaultCtaData;
    subHeadline: ReactNode;
    creditCardText: ReactNode;
};

interface Plans extends PlanCardProps, PlanData {}

interface PlanSelectorProps {
    mainHeadline: ReactNode;
    offerTitle?: ReactNode;
    plans: Plans[];
    defaultCtaData: DefaultCtaData;
}

export default function PlanSelector({
    mainHeadline,
    plans,
    offerTitle,
    defaultCtaData,
}: PlanSelectorProps): React.JSX.Element {
    const defaultPlanData = useMemo(() => {
        return {
            cta: {
                href: defaultCtaData.href,
                linkText: defaultCtaData.linkText,
                ariaLabel: defaultCtaData.ariaLabel,
            },
            subHeadline: '',
            creditCardText: '',
        };
    }, [defaultCtaData.ariaLabel, defaultCtaData.href, defaultCtaData.linkText]);

    const [selectedPlan, setSelectedPlan] = useState<PlanData>(defaultPlanData);
    const [errorMessage, setErrorMessage] = useState(false);
    const [selectedInput, setSelectedInput] = useState(false);
    const inputRef = useRef<(HTMLInputElement | null)[]>([]);

    const href = useInheritParams(selectedPlan.cta.href || '#', URL_INHERIT_PARAMS);

    useEffect(() => {
        if (plans && plans.length === 1 && selectedPlan.cta.linkText === defaultPlanData.cta.linkText) {
            setSelectedPlan({
                cta: plans[0].cta,
                subHeadline: plans[0].subHeadline,
                creditCardText: plans[0].creditCardText,
            });
        }
    }, [plans, selectedPlan.cta.linkText, defaultPlanData.cta.linkText]);

    const onChangeHandler = (index: number) => {
        if (selectedPlan.cta.linkText === plans[index].cta.linkText && !selectedInput) {
            setSelectedInput(true);
            setSelectedPlan(defaultPlanData);
            inputRef.current?.[index]?.blur();
        } else if (selectedPlan.cta.linkText === plans[index].cta.linkText && selectedInput) {
            setSelectedInput(false);
            setErrorMessage(false);
            setSelectedPlan({
                cta: plans[index].cta,
                subHeadline: plans[index].subHeadline,
                creditCardText: plans[index].creditCardText,
            });
        } else {
            setSelectedInput(false);
            setErrorMessage(false);
            setSelectedPlan({
                cta: plans[index].cta,
                subHeadline: plans[index].subHeadline,
                creditCardText: plans[index].creditCardText,
            });
        }
    };

    const handleButtonClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (selectedPlan.cta.href === '' || selectedPlan.cta.href === '?') {
            e.preventDefault();
            setErrorMessage(true);
            return;
        }

        setErrorMessage(false);
    };

    const content = plans?.map(({ headline, descriptionText, iconSource }, index: number) => {
        const isOnlyOption = plans.length === 1;
        const isSelected = isOnlyOption || selectedPlan.cta.linkText === plans[index].cta.linkText;

        return (
            <div key={index} className={inputWrapper}>
                <input
                    ref={(ref) => {
                        inputRef.current[index] = ref;
                    }}
                    type="radio"
                    name="select"
                    className={inputStyles}
                    onClick={() => onChangeHandler(index)}
                    aria-label={`Listen on ${headline}`}
                    data-tracklinktext={headline}
                    checked={isSelected}
                />
                <label>
                    <PlanCard headline={headline} descriptionText={descriptionText} iconSource={iconSource} />
                </label>
            </div>
        );
    });

    const selectedPlanText =
        selectedPlan.subHeadline !== '' || selectedPlan.creditCardText !== '' ? (
            <>
                <span>{selectedPlan.subHeadline}</span>
                <span>{selectedPlan.creditCardText}</span>
            </>
        ) : (
            <>
                Then current plan price. See <span className={offerDetailStyles}>Offer Details.</span>
            </>
        );

    return (
        <div className={mainWrapper}>
            <div className={selectorWrapper}>
                <span className={headlineStyles}>{mainHeadline}</span>
                <div className={errorMessageStyles}>
                    <InvalidFeedback validState={errorMessage ? VALIDITY.invalid : VALIDITY.valid}>
                        Please make a selection
                    </InvalidFeedback>
                </div>
                <div className={planCardWrapper}>{content}</div>
                <div className={offerSection}>
                    <div role="status" className={textContentWrapper}>
                        {offerTitle && <span className={offerTitleStyles}>{offerTitle}</span>}
                        <span className={offerPriceStyles}>{selectedPlanText}</span>
                    </div>
                    <Button
                        className={selectButtonStyles}
                        href={href}
                        variant="primary"
                        type="button"
                        onClick={handleButtonClick}
                        ariaLabel={selectedPlan.cta.ariaLabel}
                    >
                        {selectedPlan.cta.linkText}
                    </Button>
                </div>
            </div>
        </div>
    );
}
